import { CompartmentComponent, PartKey } from '@inter-ikea-storageone/range';
import {
  GuestConnectionsComponent,
  ComponentIds,
  HierarchyUtils,
  PointSeriesSubConnector,
  ProductPartComponent
} from '@inter-ikea-core/core';
import { BehaviorParameters } from 'rangeConfig/@types';
import {
  getFrontsOnFrame,
  getFrontsInScene,
  getMajorityDesign,
  addFrontLayoutToFrame
} from '../../shared/utils/frontLayouts';

const addFrontLayout = ({
  coreApi,
  rootState: {
    products: { inMarket: products }
  },
  props
}: BehaviorParameters) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  const frontLayoutProduct = products.find(
    product => product.coreProductId === props.coreProductId
  );
  if (!frontLayoutProduct) return;

  if (selectedEntity?.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    const frontEntitesOnFrame = getFrontsOnFrame(selectedEntity);
    const frontEntitesInScene = getFrontsInScene(coreApi);

    const previousFrontDesign =
      frontEntitesOnFrame.length > 0
        ? getMajorityDesign(frontEntitesOnFrame)
        : getMajorityDesign(frontEntitesInScene);

    // Add front layout to selected frame
    addFrontLayoutToFrame(
      frontLayoutProduct,
      selectedEntity,
      products,
      coreApi,
      previousFrontDesign
    );
  } else if (
    selectedEntity?.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
    selectedEntity?.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
  ) {
    // Add front layout to frame/compartment where selected door/drawer sits
    const frameAssembly = HierarchyUtils.findWithComponentInUpwardsHierarchy(
      selectedEntity,
      ComponentIds.FRAME_COMPONENT_ID
    );
    if (!frameAssembly) return;

    const frontEntitesOnFrame = getFrontsOnFrame(frameAssembly);
    const frontEntitesInScene = getFrontsInScene(coreApi);

    const previousFrontDesign =
      frontEntitesOnFrame.length > 0
        ? getMajorityDesign(frontEntitesOnFrame)
        : getMajorityDesign(frontEntitesInScene);
    const frame = HierarchyUtils.children(frameAssembly).find(
      child =>
        child.getComponent<ProductPartComponent>(ComponentIds.PRODUCT_PART_COMPONENT_ID)
          ?.partKey === PartKey.FRAME
    );

    const compartmentsInFrame = frame
      ? HierarchyUtils.children(frame).filter(child => child.hasComponent(CompartmentComponent.id))
      : [];

    const compartmentForSelectedFront = compartmentsInFrame.find(compartment => {
      const frontHostConnector = selectedEntity.getComponent<GuestConnectionsComponent>(
        ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID
      )?.highestPriorityConnection?.hostConnector as PointSeriesSubConnector;
      const compartmentConnectors = compartment.getComponent<CompartmentComponent>(
        CompartmentComponent.id
      )?.connectorsInCompartment;
      return compartmentConnectors?.includes(frontHostConnector.series?.key);
    });
    const frontPosition = coreApi.entities.getWithPosition(selectedEntity);

    addFrontLayoutToFrame(
      frontLayoutProduct,
      frameAssembly,
      products,
      coreApi,
      previousFrontDesign,
      compartmentForSelectedFront
    );

    const entityToSelect =
      getFrontsOnFrame(frameAssembly).length > 0 && frontPosition?.position
        ? coreApi.entities.getEntityClosestToPosition(
            { x: frontPosition.position.x!, y: frontPosition.position.y! },
            [ComponentIds.DOOR_COMPONENT_ID, ComponentIds.DRAWER_COMPONENT_ID]
          )
        : frameAssembly;

    if (entityToSelect) {
      coreApi.entities.select(entityToSelect);
    }
  }
};

export default addFrontLayout;
